import { useEnv } from 'liyu-pc-base/init-plugin';
import cloneDeep from 'lodash-es/cloneDeep';
import md5 from 'blueimp-md5';
import type { PageConfig } from 'liyu-pc-base/components/remotePages/typing';

export function generateMD5(str: string): string {
  return md5(str);
}

const getI18nId = (modalType: string): string => {
  // 取md5末尾3位
  return generateMD5(modalType).slice(-3);
};

const modifyBaseColumns = (
  data: any,
  t: any,
  i18n_id: string,
  get_field_i18n_id: (field: string) => Promise<string>,
) => {
  if (!data || !Array.isArray(data)) {
    return data;
  }
  data.forEach((item: any) => {
    if (item.title) {
      item.title = t(`${i18n_id}_${item.title}`, {
        defaultValue: item.title,
      });
    }
    if (item.contentMap) {
      Object.keys(item.contentMap).forEach((key: string) => {
        item.contentMap[key] = t(
          `${i18n_id}_${get_field_i18n_id(item.dataIndex)}_${item.contentMap[key]}`,
          {
            defaultValue: item.contentMap[key],
          },
        );
      });
    }
    if (item.filters) {
      item.filters.forEach((filter: any) => {
        if (filter.text) {
          filter.text = t(`${i18n_id}_${get_field_i18n_id(item.dataIndex)}_${filter.text}`, {
            defaultValue: filter.text,
          });
        }
      });
    }
  });
  return data;
};

const modifySearchConfig = (
  data: any,
  t: any,
  i18n_id: string,
  get_field_i18n_id: (field: string) => string,
) => {
  if (data?.fields && Array.isArray(data.fields)) {
    data.fields.forEach((item: any) => {
      if (item.label) {
        item.label = t(`${i18n_id}_${item.label}`, {
          defaultValue: item.label,
        });
      }
      if (Array.isArray(item.placeholder)) {
        item.placeholder = item.placeholder.map((placeholder: string) =>
          t(`${i18n_id}_${placeholder}`, {
            defaultValue: placeholder,
          }),
        );
      } else if (item.placeholder) {
        item.placeholder = t(`${i18n_id}_${item.placeholder}`, {
          defaultValue: item.placeholder,
        });
      }
    });
  }
  if (data?.options) {
    for (const key in data.options) {
      data.options[key].forEach((item: any) => {
        if (item.name) {
          item.name = t(`${i18n_id}_${get_field_i18n_id(key)}_${item.name}`, {
            defaultValue: item.name,
          });
        }
        if (item.label) {
          item.label = t(`${i18n_id}_${get_field_i18n_id(key)}_${item.label}`, {
            defaultValue: item.label,
          });
        }
      });
    }
  }
  const rules = data?.rules;
  if (rules) {
    Object.keys(rules).forEach((key: string) => {
      rules[key].forEach((rule: any) => {
        if (rule.message) {
          rule.message = t(`${i18n_id}_${rule.message}`, {
            defaultValue: rule.message,
          });
        }
      });
    });
  }
  return data;
};

const modifyBaseSearchConditionConfigList = (
  data: any,
  t: any,
  i18n_id: string,
  get_field_i18n_id: (field: string) => string,
) => {
  if (data && Array.isArray(data)) {
    data.forEach((item: any) => {
      if (item.name) {
        item.name = t(`${i18n_id}_${item.name}`, {
          defaultValue: item.name,
        });
      }
      const group = item.group;
      if (group && Array.isArray(group)) {
        group.forEach((groupItem: any) => {
          if (groupItem.label) {
            groupItem.label = t(`${i18n_id}_${groupItem.label}`, {
              defaultValue: groupItem.label,
            });
          }
          if (groupItem.options) {
            groupItem.options.forEach((option: any) => {
              if (option.label) {
                option.label = t(
                  `${i18n_id}_${get_field_i18n_id(groupItem.field_name)}_${option.label}`,
                  {
                    defaultValue: option.label,
                  },
                );
              }
            });
          }
        });
      }
    });
  }
  return data;
};

const modifyDefaultSearchConditionConfigList = (
  data: any,
  t: any,
  i18n_id: string,
  get_field_i18n_id: (field: string) => string,
) => {
  if (data && Array.isArray(data)) {
    data.forEach((item: any) => {
      if (item && Array.isArray(item)) {
        item.forEach((field: any) => {
          if (field.label) {
            field.label = t(`${i18n_id}_${field.label}`, {
              defaultValue: field.label,
            });
          }
        });
      }
    });
  }
  return data;
};

const modifyConditionGroupConfigList = (
  data: any,
  t: any,
  i18n_id: string,
  get_field_i18n_id: (field: string) => string,
) => {
  if (data && Array.isArray(data)) {
    data.forEach((item: any) => {
      if (item.label) {
        item.label = t(`${i18n_id}_${item.label}`, {
          defaultValue: item.label,
        });
      }
    });
  }
  return data;
};

const modifyFormModalConfig = (
  data: any,
  t: any,
  i18n_id: string,
  get_field_i18n_id: (field: string) => string,
) => {
  const dynamicFieldList = data?.dynamicFieldList;
  if (dynamicFieldList && Array.isArray(dynamicFieldList)) {
    dynamicFieldList.forEach((item: any) => {
      if (item.group_name) {
        item.group_name = t(`${i18n_id}_${item.group_name}`, {
          defaultValue: item.group_name,
        });
      }
      const children = item.children;
      if (children && Array.isArray(children)) {
        children.forEach((child: any) => {
          if (child.label) {
            child.label = t(`${i18n_id}_${child.label}`, {
              defaultValue: child.label,
            });
          }
          const placeholder = child.placeholder;
          if (placeholder && Array.isArray(placeholder)) {
            child.placeholder = placeholder.map((placeholder: string) =>
              t(`${i18n_id}_${placeholder}`, {
                defaultValue: placeholder,
              }),
            );
          } else if (placeholder) {
            child.placeholder = t(`${i18n_id}_${placeholder}`, {
              defaultValue: placeholder,
            });
          }
        });
      }
    });
  }
  const rules = data?.rules;
  if (rules) {
    Object.keys(rules).forEach((key: string) => {
      rules[key].forEach((rule: any) => {
        if (rule.message) {
          rule.message = t(`${i18n_id}_${rule.message}`, {
            defaultValue: rule.message,
          });
        }
      });
    });
  }
  const options = data?.options;
  if (options) {
    Object.keys(options).forEach((key: string) => {
      options[key].forEach((item: any) => {
        if (item.name) {
          item.name = t(`${i18n_id}_${get_field_i18n_id(key)}_${item.name}`, {
            defaultValue: item.name,
          });
        }
        if (item.label) {
          item.label = t(`${i18n_id}_${get_field_i18n_id(key)}_${item.label}`, {
            defaultValue: item.label,
          });
        }
      });
    });
  }
  return data;
};

const modifyTitleList = (
  data: any,
  t: any,
  i18n_id: string,
  get_field_i18n_id: (field: string) => string,
): any => {
  if (data?.title) {
    data.title = t(`${i18n_id}_${data.title}`, {
      defaultValue: data.title,
    });
  }
  if (data?.addModalTitle) {
    data.addModalTitle = t(`${i18n_id}_${data.addModalTitle}`, {
      defaultValue: data.addModalTitle,
    });
  }
  if (data?.updateTitle) {
    data.updateTitle = t(`${i18n_id}_${data.updateTitle}`, {
      defaultValue: data.updateTitle,
    });
  }

  return data;
};

const modifyBtnList = (
  data: any,
  t: any,
  i18n_id: string,
  get_field_i18n_id: (field: string) => string,
): any => {
  if (data && Array.isArray(data)) {
    data.forEach((item: any) => {
      if (item.name) {
        item.name = t(`${i18n_id}_${item.name}`, {
          defaultValue: item.name,
        });
      }
      const model_config = item.model_config;
      if (model_config) {
        if (model_config.title) {
          model_config.title = t(`${i18n_id}_${model_config.title}`, {
            defaultValue: model_config.title,
          });
        }
        if (model_config.content) {
          model_config.content = t(`${i18n_id}_${model_config.content}`, {
            defaultValue: model_config.content,
          });
        }
        if (model_config.form) {
          model_config.form = modifyFormModalConfig(
            model_config.form,
            t,
            i18n_id,
            get_field_i18n_id,
          );
        }
      }
    });
  }
  return data;
};

// 配置修改函数
const modifyFunc = {
  baseColumns: modifyBaseColumns,
  searchConfig: modifySearchConfig,
  base_search_condition_config_list: modifyBaseSearchConditionConfigList,
  default_search_condition_config_list: modifyDefaultSearchConditionConfigList,
  condition_group_config_list: modifyConditionGroupConfigList,
  formModalConfig: modifyFormModalConfig,
  updateFormModalConfig: modifyFormModalConfig,
  titleList: modifyTitleList,
  btn_list: modifyBtnList,
  head_btn_list: modifyBtnList,
  batch_update_list: modifyBtnList,
};

export const modify_by_i18n_config = (mergedConfig: any, t: any, modalType: string): PageConfig => {
  const env = useEnv();
  if (!env.VITE_APP_ENABLE_ZH_EN) {
    return mergedConfig;
  }

  const result = cloneDeep(mergedConfig);
  const i18n_config = result.i18n;
  Object.keys(result).forEach(key => {
    if (modifyFunc[key]) {
      let i18n_id = i18n_config?.i18n_id;
      let fields = i18n_config?.fields;
      if (!i18n_id) {
        i18n_id = getI18nId(modalType);
      }
      const get_field_i18n_id = (field: string) => {
        if (fields) {
          const field_i18n_id = fields[field]?.i18n_id;
          if (field_i18n_id) {
            return field_i18n_id;
          }
        }
        return getI18nId(field);
      };
      result[key] = modifyFunc[key](result[key], t, i18n_id, get_field_i18n_id);
    }
  });
  console.log('result====', result);
  return result;
};
